import { DATE_OPTIONS_DEFINITIONS, isNullish } from 'wflow-cdk';
import { ControlOptionValue, PanelFilterState } from 'wflow-ui';
import { FilterCondition } from 'wflow-variables';
import {
  PeppolDocumentFilter,
  PeppolFilterKey,
  PeppolListScope,
} from '../components/peppol-items/peppol-items.interface';
import { PeppolDirection } from '../interfaces/peppol-item.dto';

export function createPeppolScopeConditions(
  scope: PeppolListScope,
): FilterCondition[] {
  switch (scope) {
    case PeppolListScope.Inbox:
      return [
        {
          property: 'direction',
          operator: 'Equals',
          value: PeppolDirection.Incoming,
        },
        { property: 'archivedAt', operator: 'IsNull' },
      ];
    case PeppolListScope.Outbox:
      return [
        {
          property: 'direction',
          operator: 'Equals',
          value: PeppolDirection.Outgoing,
        },
        { property: 'archivedAt', operator: 'IsNull' },
      ];
    case PeppolListScope.Archive:
      return [{ property: 'archivedAt', operator: 'IsNotNull' }];
    default:
      return [];
  }
}

export function createPeppolFilterConditionsFromState(
  state: PanelFilterState,
): FilterCondition[] {
  return Object.entries(state).flatMap(([key, value]) => {
    if (isNullish(value) || typeof value === 'boolean') {
      return [];
    }

    return createTransactionFilterCondition(key as PeppolFilterKey, value);
  });
}

export function createTransactionFilterCondition(
  property: PeppolFilterKey,
  option: ControlOptionValue,
): FilterCondition[] {
  switch (property) {
    case 'direction':
      return [
        {
          property: 'direction',
          operator: 'Equals',
          value: option.value,
        },
      ];

    case 'status':
      return [
        {
          property: 'status',
          operator: 'Equals',
          value: option.value,
        },
      ];

    case 'document':
      return [
        option.value === PeppolDocumentFilter.Connected
          ? { property: 'document.id', operator: 'IsNotNull' }
          : { property: 'document.id', operator: 'IsNull' },
      ];

    case 'partner':
    case 'sender':
    case 'receiver':
      return [
        {
          property: 'partner.name',
          operator: 'Equals',
          value: option.value,
        },
      ];

    case 'invoiceType':
      return [
        {
          property: 'invoiceType',
          operator: 'Equals',
          value: option.value,
        },
      ];

    case 'user':
      return [
        {
          property: 'userAction.userEmail',
          operator: 'Equals',
          value: option.value,
        },
      ];

    case 'date':
      return createDateFilterCondition(option);

    default:
      return [];
  }
}

function createDateFilterCondition(
  option: ControlOptionValue,
): FilterCondition[] {
  const dateRange =
    DATE_OPTIONS_DEFINITIONS.find(
      (definition) => definition.key === option.value,
    )?.range ?? option.value;

  if (
    typeof dateRange !== 'object' ||
    dateRange === null ||
    !('from' in dateRange && 'to' in dateRange)
  ) {
    return [];
  }

  return [
    {
      property: 'createdAt',
      operator: 'Between',
      value: [
        typeof dateRange.from === 'string'
          ? dateRange.from
          : dateRange.from.toISOString(),
        typeof dateRange.to === 'string'
          ? dateRange.to
          : dateRange.to.toISOString(),
      ],
    },
  ];
}
